import { createAsyncThunk } from '@reduxjs/toolkit'

import { Status } from '../../components/create-defect/types'
import { apiGET } from '../../lib/api'
import { UserDefectsByInstallationGroup } from '../../reducers/defectsSlice'
import { Defect, DefectsFilter } from '../../types'

interface DefectsResponse {
  defects: Array<Defect>
  token?: string
}

interface DefectsByUserResponse {
  defects: UserDefectsByInstallationGroup
  token?: string
}

interface FetchDefectsError {
  message: string
}

type DefectFetchStatus = Status | 'all'

const fetchDefects = (
  status: DefectFetchStatus,
  defectsFilters?: DefectsFilter
): Promise<DefectsResponse> => {
  const urlSearchParams = new URLSearchParams({
    status,
    onlyOwnDefects: String(!!defectsFilters?.onlyOwnDefects),
  })

  if (defectsFilters?.installationGroups) {
    urlSearchParams.append('installationGroups', defectsFilters.installationGroups)
  }

  return apiGET(`defects?${urlSearchParams.toString()}`)
}

export const fetchOpenDefectsAction = createAsyncThunk<
  DefectsResponse,
  DefectsFilter | undefined,
  { rejectValue: FetchDefectsError }
>('defects/fetchOpenDefects', async (defectsFilters, thunkApi) => {
  try {
    const response = await fetchDefects('open', defectsFilters)

    return {
      defects: response.defects,
      token: response.token,
    } as DefectsResponse
  } catch (err) {
    return thunkApi.rejectWithValue({
      message: 'Failed to fetch open defects',
    })
  }
})

export const fetchClosedDefectsAction = createAsyncThunk<
  DefectsResponse,
  DefectsFilter | undefined,
  { rejectValue: FetchDefectsError }
>('defects/fetchClosedDefects', async (defectsFilters, thunkApi) => {
  try {
    const response = await fetchDefects('closed', defectsFilters)

    return {
      defects: response.defects,
      token: response.token,
    } as DefectsResponse
  } catch (err) {
    return thunkApi.rejectWithValue({
      message: 'Failed to fetch closed defects',
    })
  }
})

const fetchMoreDefects = (
  status: DefectFetchStatus,
  defectsFilters?: DefectsFilter
): Promise<DefectsResponse> => {
  const urlSearchParams = new URLSearchParams({
    status,
    token: defectsFilters?.token ?? '',
    onlyOwnDefects: String(!!defectsFilters?.onlyOwnDefects),
  })

  if (defectsFilters?.installationGroups) {
    urlSearchParams.append('installationGroups', defectsFilters.installationGroups)
  }

  return apiGET(`defects?${urlSearchParams.toString()}`)
}

export const fetchMoreOpenDefectsAction = createAsyncThunk<
  DefectsResponse,
  DefectsFilter | undefined,
  { rejectValue: FetchDefectsError }
>('defects/fetchMoreOpenDefects', async (defectsFilters, thunkApi) => {
  try {
    const response = await fetchMoreDefects('open', defectsFilters)

    return {
      defects: response.defects,
      token: response.token,
    } as DefectsResponse
  } catch (err) {
    return thunkApi.rejectWithValue({
      message: 'Failed to fetch more open defects',
    })
  }
})

export const fetchMoreClosedDefectsAction = createAsyncThunk<
  DefectsResponse,
  DefectsFilter | undefined,
  { rejectValue: FetchDefectsError }
>('defects/fetchMoreClosedDefects', async (defectsFilters, thunkApi) => {
  try {
    const response = await fetchMoreDefects('closed', defectsFilters)

    return {
      defects: response.defects,
      token: response.token,
    } as DefectsResponse
  } catch (err) {
    return thunkApi.rejectWithValue({
      message: 'Failed to fetch more closed defects',
    })
  }
})

export const fetchDefectsByUser = createAsyncThunk<
  DefectsByUserResponse,
  DefectsFilter | undefined,
  { rejectValue: FetchDefectsError }
>('defects/fetchDefectsByUser', async (defectsFilters, thunkApi) => {
  try {
    const response = await fetchDefects('all', defectsFilters)

    const grouped: UserDefectsByInstallationGroup = {}

    for (const defect of response.defects) {
      const key = `${defect.installationGroup} (${defect?.vehicleSeries || 'Tunnistamaton'})`

      if (!grouped[key]) {
        grouped[key] = {
          closed: [],
          open: [],
        }
      }

      if (defect.open) {
        grouped[key].open.push(defect)
      } else {
        grouped[key].closed.push(defect)
      }
    }

    return {
      defects: grouped,
      token: response.token,
    }
  } catch (err) {
    console.error('Failed to fetch defects by user:', err)
    return thunkApi.rejectWithValue({
      message: 'Failed to fetch defects by user',
    })
  }
})
